import { ONLINE_NAME_POOL } from '@/config/racers';

/**
 * racerNames.ts — the M101 username rules + suggestion generator (PURE).
 *
 * Racer names are globally UNIQUE usernames since M101 (owner rule
 * 2026-07-28). The FORMAT rule here mirrors the server's
 * `is_valid_username()` (migration `usernames_v1`) EXACTLY: 3-12 chars of
 * [A-Za-z0-9_-]. Display keeps the typed casing; UNIQUENESS is
 * case-insensitive and lives server-side only (unique index on
 * lower(nickname)) — the client treats availability as a server question
 * (the `username_available` RPC) and mirrors nothing but the format.
 *
 * Guest display names on the web keep the looser M80 sanitizeNickname rules
 * (local-only, never claimed); this module governs ACCOUNT usernames.
 */

export const USERNAME_MIN = 3;
/** = NICKNAME_MAX (the M80 display cap — every name surface fits 12). */
export const USERNAME_MAX = 12;
const USERNAME_RE = /^[A-Za-z0-9_-]{3,12}$/;

export function isValidUsername(name: string): boolean {
  return USERNAME_RE.test(name);
}

/** Squash arbitrary input into the username charset (may come back too short
 *  — callers validate). Derives suggestion bases from typed names. */
export function toUsernameBase(raw: string): string {
  return raw.replace(/[^A-Za-z0-9_-]/g, '').slice(0, USERNAME_MAX);
}

// Suggestion vocabulary — the ONLINE_NAME_POOL's kid-safe arcade language,
// composed fresh so a suggestion can't collide with the AI handles the
// player will actually race against. The two lists are DISJOINT (a prefix
// never doubles as a core, so "DashDash"-style rolls can't happen) and every
// word is ≤6 chars, so prefix+core always fits USERNAME_MAX without digits.
const PREFIXES = [
  'Turbo', 'Nitro', 'Drift', 'Volt', 'Blitz', 'Swift', 'Rapid', 'Golden',
  'Magic', 'Mega', 'Hyper', 'Prime', 'Rocket', 'Comet', 'Astro', 'Flash',
  'Neon', 'Solar', 'Zoom', 'Jet', 'Aero', 'Ultra', 'Wild', 'Lucky',
] as const;
const CORES = [
  'Fox', 'Wolf', 'Hawk', 'Lion', 'Tiger', 'Cobra', 'Ace', 'Star', 'King',
  'Champ', 'Racer', 'Rider', 'Pilot', 'Driver', 'Kick', 'Goal', 'Strike',
  'Keeper', 'Baller', 'Boots', 'Motor', 'Gears', 'Dash', 'Storm',
] as const;

const POOL_LOWER = new Set(ONLINE_NAME_POOL.map((n) => n.toLowerCase()));

function pick<T>(list: readonly T[], rand: () => number): T {
  return list[Math.floor(rand() * list.length) % list.length];
}

function twoDigits(rand: () => number): string {
  return String(10 + (Math.floor(rand() * 90) % 90));
}

/**
 * One fresh username suggestion (always format-valid, never an AI-pool
 * handle). Availability is the SERVER's call — the onboarding screen
 * verifies each suggestion via the availability RPC and re-rolls on a miss.
 * `rand` is injectable for deterministic tests.
 */
export function suggestUsername(rand: () => number = Math.random): string {
  for (let i = 0; i < 24; i++) {
    let name = `${pick(PREFIXES, rand)}${pick(CORES, rand)}`;
    // Digits make later suggestions likelier to be free — but only when they
    // still fit, and not every time (bare names read more "chosen").
    if (name.length <= USERNAME_MAX - 2 && rand() < 0.6) name += twoDigits(rand);
    if (!POOL_LOWER.has(name.toLowerCase())) return name;
  }
  return `Racer${twoDigits(rand)}`; // unreachable in practice — belt and braces
}

/**
 * "Similar and available"-shaped variants of a TAKEN submission (the owner's
 * taken-flow spec): the base survives recognisably, digits/underscore make it
 * unique-ish. Callers filter the result through the availability RPC and show
 * the first few that are actually free. Deduped, format-valid, never equal to
 * the base itself (case-insensitively).
 */
export function usernameCandidates(
  base: string,
  rand: () => number = Math.random,
  count = 6,
): string[] {
  let b = toUsernameBase(base);
  if (b.length < USERNAME_MIN) b = suggestUsername(rand);
  const trimTo = (n: number): string => b.slice(0, Math.max(USERNAME_MIN, n));
  const raw = [
    `${trimTo(USERNAME_MAX - 2)}${twoDigits(rand)}`,
    `${trimTo(USERNAME_MAX - 2)}${twoDigits(rand)}`,
    `${trimTo(USERNAME_MAX - 2)}_${String(Math.floor(rand() * 10) % 10)}`,
    `${trimTo(USERNAME_MAX - 1)}${String(Math.floor(rand() * 10) % 10)}`,
    suggestUsername(rand),
    suggestUsername(rand),
  ];
  const seen = new Set<string>([b.toLowerCase()]);
  const out: string[] = [];
  for (const cand of raw) {
    const key = cand.toLowerCase();
    if (!isValidUsername(cand) || seen.has(key) || POOL_LOWER.has(key)) continue;
    seen.add(key);
    out.push(cand);
    if (out.length >= count) break;
  }
  return out;
}
